import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  sanitizeIntegerString,
  clampNumericValue,
  formatUnitConversionLabel,
  LOGIC_CONFIG_UNITS,
} from '../utils/logicConfigDomain';

/**
 * Reusable, atomic UI component for entering sanitized non-negative integer values.
 * Follows Single Responsibility Principle (SRP).
 */
const NumericConfigInput = ({
  value,
  min = 0,
  max,
  unit = LOGIC_CONFIG_UNITS.SECONDS,
  onChange,
  disabled = false,
}) => {
  const initialValidVal = value !== undefined && value !== null && value >= min
    ? value
    : min;

  const [localStr, setLocalStr] = useState(String(initialValidVal));

  useEffect(() => {
    const validVal = value !== undefined && value !== null && value >= min
      ? value
      : min;
    setLocalStr(String(validVal));
  }, [value, min]);

  const handleKeyDown = (e) => {
    // Block negative signs, decimal points, exponents, and whitespace
    if (['-', '+', '.', ',', 'e', 'E', ' '].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    // Sanitize string to strictly allow digits 0-9
    const cleanStr = sanitizeIntegerString(e.target.value);
    setLocalStr(cleanStr);

    if (cleanStr !== '') {
      const clampedVal = clampNumericValue(cleanStr, min, max);
      onChange(clampedVal);
    }
  };

  const handleBlur = () => {
    if (localStr === '' || isNaN(parseInt(localStr, 10))) {
      setLocalStr(String(min));
      onChange(min);
    } else {
      const clampedVal = clampNumericValue(localStr, min, max);
      setLocalStr(String(clampedVal));
      onChange(clampedVal);
    }
  };

  const currentNumericValue = parseInt(localStr, 10) || 0;
  const conversionLabel = formatUnitConversionLabel(currentNumericValue, unit);

  return (
    <div className="d-flex align-items-center">
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={localStr}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        className="config-number-input"
      />
      <span className="config-conversion-badge">{conversionLabel}</span>
    </div>
  );
};

NumericConfigInput.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  unit: PropTypes.oneOf([LOGIC_CONFIG_UNITS.SECONDS, LOGIC_CONFIG_UNITS.MINUTES]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default NumericConfigInput;
